import React, { useEffect } from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import imgLoginBackground from '../../../../assets/img_login_background_image.jpg';
import imgColorP200 from '../../../../assets/img_color_p200.jpg';
import imgColorP300 from '../../../../assets/img_color_p300.jpg';
import imgColorP400 from '../../../../assets/img_color_p400.jpg';
import imgColorP500 from '../../../../assets/img_color_p500.jpg';
import imgColorP600 from '../../../../assets/img_color_p600.jpg';
import imgColorS75 from '../../../../assets/img_color_s75.jpg';
import imgColorS200 from '../../../../assets/img_color_s200.jpg';
import imgColorS300 from '../../../../assets/img_color_s300.jpg';
import imgColorS400 from '../../../../assets/img_color_s400.jpg';
import imgColorSf from '../../../../assets/img_color_sf.jpg';
import imgColorLsf from '../../../../assets/img_color_lsf.jpg';
import imgColorAl from '../../../../assets/img_color_al.jpg';
import imgColorLal from '../../../../assets/img_color_lal.jpg';
import imgColorDn from '../../../../assets/img_color_dn.jpg';
import imgColorLdn from '../../../../assets/img_color_ldn.jpg';
import { CustomColourPicker } from '../../../../components/CustomColourPicker';
import { Skeleton } from 'primereact/skeleton';
import OpenAPI from '../../../../endpoint/Openapi';
import useTenantStore, { selectTenantId } from '../../../../stores/TenantStore';
import { noop } from '../../../../utils/GeneralUtils';
import { type components, type paths } from '../../../../endpoint/Schema';
import { ReactComponent as DiscardIcon } from '../../../../assets/ic_delete.svg';
import { ReactComponent as ResetIcon } from '../../../../assets/ic_reset.svg';
import DropdownMenu from '../../../../components/DropDownMenu';
import classNames from 'classnames';
import { type CustomError } from '../../../../models/Error';

const ScreenshotMapper: Partial<{
  [key in components['schemas']['TenantAppColorEnum']]: string;
}> = {
  P200: imgColorP200,
  P300: imgColorP300,
  P400: imgColorP400,
  P500: imgColorP500,
  P600: imgColorP600,
  S75: imgColorS75,
  S200: imgColorS200,
  S300: imgColorS300,
  S400: imgColorS400,
  LBg: imgLoginBackground,
  Sf: imgColorSf,
  LSf: imgColorLsf,
  Al: imgColorAl,
  LAl: imgColorLal,
  Dn: imgColorDn,
  LDn: imgColorLdn,
};

const ITEMS = [
  {
    id: 0,
    Icon: DiscardIcon,
    title: 'Discard Changes',
    iconClassName: 'stroke-gray-400',
    titleClassName: 'text-black',
  },
  {
    id: 1,
    Icon: ResetIcon,
    title: 'Reset to Default',
    iconClassName: 'stroke-red-600',
    titleClassName: 'text-red-600',
  },
];

function Colours() {
  const tenantId = useTenantStore(selectTenantId);
  const { setTenantConfig } = useTenantStore();
  const [coloursList, setColoursList] = React.useState<
    paths['/api/v2/admin/tenant/app-color']['get']['responses']['200']['content']['application/json']
  >([]);
  const [loading, setLoading] = React.useState(false);
  const [screenshotImage, setScreenshotImage] = React.useState(
    () => imgColorP200,
  );
  const toast = React.useRef<Toast>(null);

  const getColors = React.useCallback(async () => {
    setLoading(true);

    try {
      const res = await OpenAPI.GET('/api/v2/admin/tenant/app-color', {
        params: { query: { tenantId } },
      });

      setColoursList(res.data ?? []);
    } catch {}

    setLoading(false);
  }, [tenantId]);

  useEffect(() => {
    getColors().catch(noop);
  }, [getColors]);

  async function saveData() {
    if (!tenantId) return;

    setLoading(true);

    try {
      const res = await OpenAPI.PUT('/api/v2/admin/tenant/app-color', {
        params: { query: { tenantId } },
        body: coloursList,
      });

      // @ts-expect-error
      if (res.error) throw res.error;

      try {
        const { data } = await OpenAPI.GET('/api/v2/admin/tenant', {
          params: {
            query: {
              tenantId,
            },
          },
        });
        if (data) setTenantConfig(data);
      } catch {}

      toast.current?.show({
        severity: 'success',
        summary: `Saved successfully`,
        life: 3000,
      });
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error occurred',
        detail:
          typeof err === 'string'
            ? err
            : err instanceof Error
              ? err.message
              : (err as CustomError)?.message || 'Unknown error',
        life: 3000,
      });
    }

    setLoading(false);
  }

  async function resetColors() {
    if (!tenantId) return;

    setLoading(true);

    try {
      const res = await OpenAPI.POST('/api/v2/admin/tenant/reset-color', {
        params: { query: { tenantId } },
      });

      // @ts-expect-error
      if (res.error) throw res.error;

      await getColors();

      toast.current?.show({
        severity: 'success',
        summary: `Colors reset successfully`,
        life: 3000,
      });
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error occurred',
        detail:
          typeof err === 'string'
            ? err
            : err instanceof Error
              ? err.message
              : 'Unknown error',
        life: 3000,
      });
    }

    setLoading(false);
  }

  const handleChangeScreenshot =
    (type?: components['schemas']['TenantAppColorEnum']) => () => {
      const mappedImage = type ? ScreenshotMapper[type] : undefined;
      setScreenshotImage(mappedImage ?? imgColorP200);
    };

  return (
    <div>
      {loading ? (
        <Skeleton width="100rem" height="100rem"></Skeleton>
      ) : (
        <div>
          <div className="flex flex-row w-full items-start gap-4 max-md:flex-col max-md:gap-8 max-md:items-center">
            <div className="flex flex-col mr-4 w-[50%] max-xl:w-[90%] max-w-[512px] bg-zinc-700 rounded-3xl max-md:mr-0 max-md:w-full max-md:max-w-[320px]">
              <img src={screenshotImage} />
            </div>

            <div className="flex flex-col justify-center w-full px-6 py-8 rounded-3xl shadow-[0px_4px_12px_rgba(0,0,0,0.1)] text-base leading-6 text-black bg-white">
              <div className="text-3xl font-medium">Colors</div>

              <div className="flex flex-row flex-wrap w-full gap-x-4 gap-y-8">
                {coloursList?.map((colour, i) => (
                  <CustomColourPicker
                    key={`color_${colour.type}_${i}`}
                    colour={colour}
                    onFocus={handleChangeScreenshot(colour.type)}
                    onChange={(type, value) => {
                      setColoursList((prev) =>
                        prev.map((c) =>
                          c.type === type ? { ...c, value } : c,
                        ),
                      );
                    }}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-row items-center w-full px-6 py-6 justify-end">
            <DropdownMenu
              label="Discard"
              items={ITEMS}
              renderItem={({ Icon, title, iconClassName, titleClassName }) => (
                <div className="flex items-center w-64">
                  <Icon className={classNames('mr-3', iconClassName)} />
                  <span className={classNames('text-xl', titleClassName)}>
                    {title}
                  </span>
                </div>
              )}
              onItemSelect={async (item) => {
                try {
                  if (item.id === 0) {
                    // discard
                    await getColors();

                    toast.current?.show({
                      severity: 'success',
                      summary: `Changes have been discarded`,
                      life: 3000,
                    });
                  } else if (item.id === 1) {
                    // reset
                    resetColors().catch(noop);
                  }
                } catch {}
              }}
            />

            <Button
              label="Save"
              className="text-2xl px-14 py-3 ml-2 bg-[#F1B51C] hover:bg-bluegray-400 text-black border-none"
              onClick={saveData}
            />
          </div>
        </div>
      )}
      <Toast ref={toast} className="mt-header-height" />
    </div>
  );
}

export default Colours;
